/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import '@neo4j-ndl/base/lib/neo4j-ds-styles.css';

import {
  DocumentIconOutline,
  FolderIconSolid,
  PhotoIconOutline,
} from '@neo4j-ndl/react/icons';
import { TreeView } from '@neo4j-ndl/react/next';
import { useCallback, useMemo, useRef, useState } from 'react';

type TreeNode = {
  id: string;
  label: string;
  children?: TreeNode[];
};

const TREE_DATA: TreeNode[] = [
  {
    children: [
      {
        children: [
          { id: 'app-tsx', label: 'App.tsx' },
          { id: 'index-tsx', label: 'index.tsx' },
          { id: 'styles-css', label: 'styles.css' },
        ],
        id: 'frontend',
        label: 'Frontend',
      },
      {
        children: [
          { id: 'server-ts', label: 'server.ts' },
          { id: 'routes-ts', label: 'routes.ts' },
        ],
        id: 'backend',
        label: 'Backend',
      },
      { id: 'readme-md', label: 'README.md' },
    ],
    id: 'projects',
    label: 'Projects',
  },
  {
    children: [
      {
        children: [
          { id: 'beach-jpg', label: 'beach.jpg' },
          { id: 'sunset-jpg', label: 'sunset.jpg' },
        ],
        id: 'vacation',
        label: 'Vacation',
      },
      { id: 'profile-png', label: 'profile.png' },
    ],
    id: 'photos',
    label: 'Photos',
  },
  {
    children: [
      { id: 'resume-pdf', label: 'resume.pdf' },
      { id: 'cover-letter-pdf', label: 'cover-letter.pdf' },
      { id: 'notes-txt', label: 'notes.txt' },
    ],
    id: 'documents',
    label: 'Documents',
  },
  {
    children: [
      {
        children: [
          { id: 'chill-m3u', label: 'chill.m3u' },
          { id: 'workout-m3u', label: 'workout.m3u' },
        ],
        id: 'playlists',
        label: 'Playlists',
      },
      { id: 'favorites-m3u', label: 'favorites.m3u' },
    ],
    id: 'music',
    label: 'Music',
  },
  { id: 'todo-txt', label: 'TODO.txt' },
];

const LOAD_DELAY_MS = 600;

function iconForNode(node: TreeNode) {
  if (node.children) {
    return <FolderIconSolid />;
  }
  if (/\.(jpg|jpeg|png|gif|svg)$/i.test(node.label)) {
    return <PhotoIconOutline />;
  }
  return <DocumentIconOutline />;
}

type NodeState = {
  isExpanded: boolean;
  isLoading: boolean;
  hasLoaded: boolean;
};

const INITIAL_NODE_STATE: NodeState = {
  hasLoaded: false,
  isExpanded: false,
  isLoading: false,
};

function useTreeState() {
  const [nodeStates, setNodeStates] = useState<Record<string, NodeState>>({});
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const getNodeState = useCallback(
    (id: string): NodeState => nodeStates[id] ?? INITIAL_NODE_STATE,
    [nodeStates],
  );

  const handleExpand = useCallback((id: string, expanded: boolean) => {
    setNodeStates((prev) => {
      const current = prev[id] ?? INITIAL_NODE_STATE;
      if (expanded && !current.hasLoaded) {
        if (timers.current[id]) {
          clearTimeout(timers.current[id]);
        }
        timers.current[id] = setTimeout(() => {
          setNodeStates((prev) => ({
            ...prev,
            [id]: { hasLoaded: true, isExpanded: true, isLoading: false },
          }));
          delete timers.current[id];
        }, LOAD_DELAY_MS);
        return {
          ...prev,
          [id]: { hasLoaded: false, isExpanded: true, isLoading: true },
        };
      }
      return { ...prev, [id]: { ...current, isExpanded: expanded } };
    });
  }, []);

  const handleSelect = useCallback(
    (id: string) => setSelectedId((prev) => (prev === id ? null : id)),
    [],
  );

  return { getNodeState, handleExpand, handleSelect, selectedId };
}

type RenderContext = {
  getNodeState: (id: string) => NodeState;
  selectedId: string | null;
  handleExpand: (id: string, expanded: boolean) => void;
  handleSelect: (id: string) => void;
};

function renderNode(node: TreeNode, ctx: RenderContext) {
  const hasChildren =
    node.children !== undefined &&
    node.children !== null &&
    node.children.length > 0;
  const state = ctx.getNodeState(node.id);

  return (
    <TreeView.Item
      key={node.id}
      title={node.label}
      hasChildren={hasChildren}
      isExpanded={state.isExpanded}
      isLoading={state.isLoading}
      isSelected={ctx.selectedId === node.id}
      leadingVisual={iconForNode(node)}
      onExpandedChange={(expanded: boolean) =>
        ctx.handleExpand(node.id, expanded)
      }
      onSelectedChange={() => ctx.handleSelect(node.id)}
    >
      {hasChildren && state.isLoading && !state.hasLoaded && (
        <TreeView.SkeletonItem rows={2} />
      )}
      {/* Rendering children only while expanded keeps collapsed subtrees out
          of the DOM, so large trees only pay for the rows the user can see.
          All item state lives outside the tree (keyed by node id), so nothing
          is lost when a subtree unmounts on collapse. */}
      {hasChildren &&
        state.isExpanded &&
        state.hasLoaded &&
        node.children!.map((child) => renderNode(child, ctx))}
    </TreeView.Item>
  );
}

const Component = () => {
  const { getNodeState, selectedId, handleExpand, handleSelect } =
    useTreeState();

  const ctx = useMemo<RenderContext>(
    () => ({ getNodeState, handleExpand, handleSelect, selectedId }),
    [getNodeState, selectedId, handleExpand, handleSelect],
  );

  return (
    <TreeView ariaLabel="Lazy-loaded file explorer">
      {TREE_DATA.map((node) => renderNode(node, ctx))}
    </TreeView>
  );
};

export default Component;
